/**
 * splitTopLevel — token-aware splitters for clause bodies. Used by the basic
 * SELECT renderer to chop a clause body into items (SELECT items, FROM items,
 * GROUP BY / ORDER BY columns) or filter pills (WHERE / HAVING fragments).
 * Pure, structural — no regex parsing (VQE-RO9). Reuses `tokenSkip` so all
 * dialect-specific quoting, comments, and string literals are skipped.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import { isWordBoundaryBefore } from '../insert/insertParseHelpers';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

export interface SplitItem {
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

export interface BooleanFragment {
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly leadingConnector: 'AND' | 'OR' | null;
}

export function splitTopLevelCommas(
  source: string,
  start: number,
  endExclusive: number,
  options: ParseOptions,
): SplitItem[] {
  const ctx = skipContextFor(options);
  const items: SplitItem[] = [];
  let paren = 0;
  let segmentStart = start;
  let i = start;
  while (i < endExclusive) {
    const skipped = trySkipNonCode(source, i, endExclusive, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (c === ',' && paren === 0) {
      items.push(makeItem(source, segmentStart, i));
      segmentStart = i + 1;
      i++;
      continue;
    }
    i++;
  }
  items.push(makeItem(source, segmentStart, endExclusive));
  return items.filter((item) => item.text.length > 0);
}

function makeItem(source: string, start: number, end: number): SplitItem {
  const raw = source.slice(start, end);
  const text = raw.trim();
  // adjust start/end to skip surrounding whitespace
  let s = start;
  while (s < end && /\s/.test(source[s])) s++;
  let e = end;
  while (e > s && /\s/.test(source[e - 1])) e--;
  return { start: s, end: e, text };
}

export function splitTopLevelBoolean(
  source: string,
  start: number,
  endExclusive: number,
  options: ParseOptions,
): BooleanFragment[] {
  const ctx = skipContextFor(options);
  const fragments: BooleanFragment[] = [];
  let paren = 0;
  let segmentStart = start;
  let segmentConnector: 'AND' | 'OR' | null = null;
  let i = start;

  function flush(end: number, nextConnector: 'AND' | 'OR' | null): void {
    const raw = source.slice(segmentStart, end);
    const text = raw.trim();
    if (text.length > 0) {
      let s = segmentStart;
      while (s < end && /\s/.test(source[s])) s++;
      let e = end;
      while (e > s && /\s/.test(source[e - 1])) e--;
      fragments.push({ start: s, end: e, text, leadingConnector: segmentConnector });
    }
    segmentConnector = nextConnector;
  }

  while (i < endExclusive) {
    const skipped = trySkipNonCode(source, i, endExclusive, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }

    if (paren === 0 && IDENT_START.test(c) && isWordBoundaryBefore(source, i)) {
      const wStart = i;
      let j = i + 1;
      while (j < endExclusive && IDENT_CONT.test(source[j])) j++;
      const word = source.slice(wStart, j).toUpperCase();
      if (word === 'AND' || word === 'OR') {
        flush(wStart, word);
        segmentStart = j;
        i = j;
        continue;
      }
      i = j;
      continue;
    }

    i++;
  }
  flush(endExclusive, null);
  return fragments;
}

